package org.teddapp.project.ui.finance

/**
 * L'évaluateur des lignes de simulation. Une ligne est une expression telle que tapée :
 * « 2 500 × 3 », « -15 000 », « 10% de 200 000 », « (1 200 + 800) / 2 ». Chaque appareil
 * évalue la même expression avec ce même code, la valeur n'est jamais stockée.
 *
 * Arithmétique d'affichage, pas une règle du noyau : le résultat est arrondi à l'unité.
 *
 * Grammaire :
 *   expr   := term (('+' | '-') term)*
 *   term   := factor (('×' | '*' | '/' | '÷' | 'de') factor)*
 *   factor := ('-' | '+') factor | number ['%'] | '(' expr ')'
 *
 * « N% » vaut N/100 ; « N% de X » et « X × N% » valent donc tous deux N × X / 100.
 * Les espaces, espaces fines et apostrophes séparent les milliers ; le point ou la
 * virgule marque les décimales.
 */
object SimulationMath {

    /** La valeur d'une expression, ou null si elle ne se lit pas. */
    fun evaluate(expression: String): Long? {
        val parser = Parser(expression)
        val value = parser.expr() ?: return null
        if (!parser.atEnd()) return null
        if (value.isNaN() || value.isInfinite()) return null
        return kotlin.math.round(value).toLong()
    }

    /** Le total d'un brouillon : la somme des lignes lisibles, les autres ne comptent pas. */
    fun total(expressions: List<String>): Long = expressions.sumOf { evaluate(it) ?: 0L }

    /** Vrai si [expression] a un sens : vide ne compte pas comme une erreur. */
    fun isBlank(expression: String): Boolean = expression.isBlank()

    private class Parser(source: String) {
        private val s = source.replace(' ', ' ').replace(' ', ' ').replace(' ', ' ')
        private var i = 0

        fun atEnd(): Boolean {
            skip()
            return i >= s.length
        }

        private fun skip() {
            while (i < s.length && s[i] == ' ') i++
        }

        private fun peek(): Char? = if (i < s.length) s[i] else null

        fun expr(): Double? {
            var left = term() ?: return null
            while (true) {
                skip()
                when (peek()) {
                    '+' -> { i++; left += term() ?: return null }
                    '-', '−', '–' -> { i++; left -= term() ?: return null }
                    else -> return left
                }
            }
        }

        private fun term(): Double? {
            var left = factor() ?: return null
            while (true) {
                skip()
                when {
                    peek() == '×' || peek() == '*' || peek() == 'x' || peek() == 'X' -> { i++; left *= factor() ?: return null }
                    peek() == '/' || peek() == '÷' || peek() == ':' -> {
                        i++
                        val d = factor() ?: return null
                        if (d == 0.0) return null
                        left /= d
                    }
                    s.startsWith("de ", i) || s.startsWith("de\u0000", i) -> { i += 2; left *= factor() ?: return null }
                    else -> return left
                }
            }
        }

        private fun factor(): Double? {
            skip()
            return when (peek()) {
                null -> null
                '-', '−', '–' -> { i++; factor()?.let { -it } }
                '+' -> { i++; factor() }
                '(' -> {
                    i++
                    val inner = expr() ?: return null
                    skip()
                    if (peek() != ')') return null
                    i++
                    percent(inner)
                }
                else -> number()?.let { percent(it) }
            }
        }

        /** Un « % » qui suit divise par cent. */
        private fun percent(value: Double): Double {
            skip()
            if (peek() == '%') { i++; return value / 100.0 }
            return value
        }

        private fun number(): Double? {
            skip()
            val start = i
            val digits = StringBuilder()
            var seenDot = false
            while (i < s.length) {
                val c = s[i]
                when {
                    c.isDigit() -> digits.append(c)
                    c == ' ' || c == '\'' || c == '’' -> {
                        // Séparateur de milliers : suivi de trois chiffres exactement,
                        // sinon « 2500 3 » se lirait 25 003.
                        if (digits.isEmpty() || seenDot || !groupFollows(i + 1)) break
                    }
                    (c == '.' || c == ',') && !seenDot -> {
                        if (digits.isEmpty() || i + 1 >= s.length || !s[i + 1].isDigit()) break
                        seenDot = true
                        digits.append('.')
                    }
                    else -> break
                }
                i++
            }
            if (digits.isEmpty()) { i = start; return null }
            return digits.toString().toDoubleOrNull()
        }

        /** Trois chiffres à partir de [at], puis autre chose qu'un chiffre. */
        private fun groupFollows(at: Int): Boolean {
            if (at + 3 > s.length) return false
            for (k in 0 until 3) if (!s[at + k].isDigit()) return false
            return at + 3 >= s.length || !s[at + 3].isDigit()
        }
    }
}
